import React from 'react';
import { Box, Button, Divider, Grid, IconButton, TextField, Typography, CircularProgress, Autocomplete } from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Clear } from '@mui/icons-material';
import { color } from '../../../StyleData/Config';
import { axiosPrivate } from '../../../axios/axios';
import { masterAPI } from '../dataConfig';
import { toast } from 'react-toastify';
import ErrorProps from '../../../Component/Errorprops';

const ModelMasterModal = ({ mode, handleCloseClick, editData, onRefresh }) => {
  const [loading, setLoading] = React.useState(false);
  const [assetTypes, setAssetTypes] = React.useState([]);
  const [technicalSpecs, setTechnicalSpecs] = React.useState([]);
  const [selectedAssetType, setSelectedAssetType] = React.useState(null);
  const [selectedTechnicalSpec, setSelectedTechnicalSpec] = React.useState(null);

  React.useEffect(() => {
    fetchAssetTypes();
    fetchTechnicalSpecs();
  }, []);

  React.useEffect(() => {
    if (mode === 'Edit' && editData && assetTypes.length > 0 && technicalSpecs.length > 0) {
      const assetType = assetTypes.find(at => at.id === editData.assetTypeId);
      const techSpec = technicalSpecs.find(ts => ts.value === editData.technicalSpecificationId);
      setSelectedAssetType(assetType || null);
      setSelectedTechnicalSpec(techSpec || null);
    }
  }, [mode, editData, assetTypes, technicalSpecs]);

  const fetchAssetTypes = async () => {
    try {
      const response = await axiosPrivate.get('/assettype/getall');
      const assetTypesData = response.data.result || response.data || [];
      setAssetTypes(assetTypesData);
    } catch (error) {
      console.error('Error fetching asset types:', error);
      setAssetTypes([]);
    }
  };

  const fetchTechnicalSpecs = async () => {
    try {
       const tech = masterAPI["selectList"].techspecbyType;
      const response = await axiosPrivate.get(`${tech}/Asset`);
      const technicalSpecsData = response.data  || [];
      setTechnicalSpecs(technicalSpecsData);
    } catch (error) {
      console.error('Error fetching technical specifications:', error);
      setTechnicalSpecs([]);
    }
  };

  const initialValue = {
    modelName: mode === 'Edit' && editData ? editData.modelName : '',
    code: mode === 'Edit' && editData ? editData.code : '',
    technicalSpecificationId: mode === 'Edit' && editData ? editData.technicalSpecificationId : '',
    assetTypeId: mode === 'Edit' && editData ? editData.assetTypeId : '',
  };

  const validationSchema = Yup.object().shape({
    modelName: Yup.string().required('Model Name is required'),
    code: Yup.string().required('Code is required'),
    technicalSpecificationId: Yup.string().required('Technical Specification is required'),
    assetTypeId: Yup.string().required('Asset Type is required'),
  });

  const onSubmit = async (values, { setSubmitting }) => {

    setLoading(true);
    try {
      const submitData = {
        ...values,
        assetTypeId: selectedAssetType.id,
        technicalSpecificationId: selectedTechnicalSpec.value,
      };

      if (mode === 'Edit' && editData) {
        const updateLink = masterAPI["master_model"].update;
        await axiosPrivate.put(updateLink, { ...submitData, id: editData.id });
        toast.success('Model updated successfully');
      } else {
        const createLink = masterAPI["master_model"].create;
        await axiosPrivate.post(createLink, submitData);
        toast.success('Model created successfully');
      }
      handleCloseClick();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error saving model:', err);
      const errorMessage = err.response?.data?.failureMessage ||
                          err.response?.data?.message ||
                          'Error saving model';
      toast.error(errorMessage);
    }
    setLoading(false);
    setSubmitting(false);
  };

  return (
    <Box sx={{ p: color.masterPageDialogPadding }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Typography sx={{ color: color.masterDialogTitleColor, fontSize: color.masterDialogFontSize }}>
          {mode === 'Edit' ? `${editData?.code || ''} - ${editData?.modelName || ''}` : 'New Model'}
        </Typography>
        <IconButton onClick={handleCloseClick}>
          <Clear />
        </IconButton>
      </Box>
      <Divider sx={{ border: '1px solid rgb(0 0 0 / 17%)', mb: '15px' }} />
      <Formik
        initialValues={initialValue}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ setFieldValue }) => (
          <Form>
            <Grid container spacing={'1rem'}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>
                  Model Code <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Field as={TextField} size={'small'} type="text" name="code" placeholder="Enter Code" fullWidth sx={{ '.MuiInputBase-input': { '&::placeholder': { opacity: 1, color: color.placeholderColor, fontSize: '13px' } } }} />
                <ErrorMessage name="code">{(error) => <ErrorProps>{error}</ErrorProps>}</ErrorMessage>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>
                  Model Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Field as={TextField} size={'small'} type="text" name="modelName" placeholder="Enter Model Name" fullWidth sx={{ '.MuiInputBase-input': { '&::placeholder': { opacity: 1, color: color.placeholderColor, fontSize: '13px' } } }} />
                <ErrorMessage name="modelName">{(error) => <ErrorProps>{error}</ErrorProps>}</ErrorMessage>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>
                  Technical Specification <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Autocomplete
                  options={technicalSpecs}
                  getOptionLabel={(option) => option.text || ''}
                  value={selectedTechnicalSpec}
                  onChange={(_, newValue) => {
                    setSelectedTechnicalSpec(newValue);
                    setFieldValue('technicalSpecificationId', newValue ? newValue.value : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select Technical Specification"
                      sx={{ '.MuiInputBase-input': { '&::placeholder': { opacity: 1, color: color.placeholderColor, fontSize: '13px' } } }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  fullWidth
                />
                <ErrorMessage name="technicalSpecificationId">{(error) => <ErrorProps>{error}</ErrorProps>}</ErrorMessage>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: color.labelFontWeight, mb: color.labelmb, fontSize: color.labelFont, color: color.labelColor }}>
                  Asset Type <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Autocomplete
                  options={assetTypes}
                  getOptionLabel={(option) => option.assetTypeName || ''}
                  value={selectedAssetType}
                  onChange={(_, newValue) => {
                    setSelectedAssetType(newValue);
                    setFieldValue('assetTypeId', newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select Asset Type"
                      sx={{ '.MuiInputBase-input': { '&::placeholder': { opacity: 1, color: color.placeholderColor, fontSize: '13px' } } }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  fullWidth
                />
                <ErrorMessage name="assetTypeId">{(error) => <ErrorProps>{error}</ErrorProps>}</ErrorMessage>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <Button variant="contained" sx={{ backgroundColor: color.TableCancelButton, color: color.CancelTextColor, textTransform: 'none', '&:hover': { backgroundColor: color.TableCancelButton } }} onClick={handleCloseClick}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading} sx={{ textTransform: 'none' }}>
                  {loading ? <CircularProgress size={24} /> : mode === 'Edit' ? 'Update' : 'Submit'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ModelMasterModal;

